import React from 'react';
import { ArrowDownRight, ArrowUpRight, Award } from 'lucide-react';
import type { PriceStats } from './stats';
import { formatCurrencyPrice } from '../../utils/currency';

export interface PriceStatsCardsProps {
  stats: PriceStats;
  currency?: string | null;
}

export const PriceStatsCards: React.FC<PriceStatsCardsProps> = ({ stats, currency }) => {
  const isDiscounted = stats.totalDropPct < 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {/* Current Price */}
      <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-800">
        <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          Current Price
        </span>
        <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100">
          {formatCurrencyPrice(stats.currentPrice, currency)}
        </div>
      </div>

      {/* All-time Low */}
      <div className="p-3.5 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-800/60">
        <div className="flex items-center justify-between mb-1">
          <span className="block text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
            All-Time Low
          </span>
          <Award className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="text-lg sm:text-xl font-bold text-emerald-700 dark:text-emerald-300">
          {formatCurrencyPrice(stats.lowestPrice, currency)}
        </div>
      </div>

      {/* All-time High */}
      <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-800">
        <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          All-Time High
        </span>
        <div className="text-lg sm:text-xl font-bold text-slate-700 dark:text-slate-300">
          {formatCurrencyPrice(stats.highestPrice, currency)}
        </div>
      </div>

      {/* Net Change */}
      <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-800">
        <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          Overall Trend
        </span>
        <div className="flex items-center gap-1 text-lg sm:text-xl font-bold">
          {isDiscounted ? (
            <>
              <ArrowDownRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-emerald-600 dark:text-emerald-400">
                {Math.abs(Math.round(stats.totalDropPct))}%
              </span>
            </>
          ) : stats.totalDropPct > 0 ? (
            <>
              <ArrowUpRight className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
              <span className="text-rose-600 dark:text-rose-400">
                +{Math.round(stats.totalDropPct)}%
              </span>
            </>
          ) : (
            <span className="text-slate-600 dark:text-slate-400">0%</span>
          )}
        </div>
      </div>
    </div>
  );
};
